'use client';

import { useState } from 'react';
import { School, Clock, PauseCircle } from 'lucide-react';
import { mediaUrl } from '@/utils/mediaUrl';

/**
 * The hero badge on the holding page: the school's logo — shown on its
 * own, large, with nothing boxing it in — when it has one and it loads;
 * the generic school icon in a white card otherwise. A tiny client
 * island — "the image failed" is only knowable in the browser (onError),
 * and the surrounding page stays a Server Component.
 *
 * The small amber status badge (clock = coming soon, pause = unavailable)
 * belongs to the fallback card only — a real logo is shown clean, with
 * nothing overlapping it.
 *
 * Re-keyed on `logoUrl` so a corrected logo doesn't stay stuck on the
 * fallback after a republish.
 */
export default function SchoolLogoBadge({ logoUrl, schoolName, reason }) {
    const [failed, setFailed] = useState(false);
    const src = mediaUrl(logoUrl);
    const showLogo = Boolean(src) && !failed;

    if (showLogo) {
        return (
            // Plain <img>: logos live on the media service at arbitrary
            // hosts, and next/image would need each one whitelisted.
            // eslint-disable-next-line @next/next/no-img-element
            <img
                key={src}
                src={src}
                alt={schoolName ? `${schoolName} logo` : 'School logo'}
                className="block h-28 max-w-[260px] object-contain"
                onError={() => setFailed(true)}
                data-testid="landing-unavailable-logo"
            />
        );
    }

    const StatusIcon = reason === 'inactive' || reason === 'expired' ? PauseCircle : Clock;
    return (
        <div className="relative inline-block">
            <div className="bg-white p-7 rounded-[2rem] shadow-sm ring-1 ring-brand-100">
                <School size={56} className="text-brand-500" aria-hidden="true" />
            </div>
            <div className="absolute -bottom-2 -right-2 bg-amber-100 p-2.5 rounded-2xl border-4 border-white shadow-sm">
                <StatusIcon size={20} className="text-amber-600" aria-hidden="true" />
            </div>
        </div>
    );
}
